/**
 * Contabilidad Dashboard - Finance 360
 * Overview of accounting status, pending items, and key metrics
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpen,
  FileText,
  Calculator,
  TrendingUp,
  AlertTriangle,
  CheckCircle2,
  Clock,
  ArrowRight,
  Building2,
  RefreshCw,
  Wallet,
  PieChart
} from 'lucide-react';
import { contabilidadApi } from '../../utils/api';
import useAuthStore from '../../store/authStore';
import { LoadingPage } from '../../components/LoadingSpinner';
import EmptyState from '../../components/EmptyState';

export default function ContabilidadDashboard() {
  const { currentEmisor } = useAuthStore();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (currentEmisor) {
      loadData();
    }
  }, [currentEmisor]);

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await contabilidadApi.getDashboard();
      setData(response.data.data);
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CR', {
      style: 'currency',
      currency: 'CRC',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  if (!currentEmisor) {
    return (
      <EmptyState
        icon={BookOpen}
        title="Seleccione una empresa"
        description="Use el selector en la parte superior para elegir la empresa."
      />
    );
  }

  if (loading) {
    return <LoadingPage />;
  }

  // Check if plan de cuentas exists
  if (!data?.balances?.activo) {
    return (
      <div className="max-w-2xl mx-auto mt-12">
        <div className="bg-white rounded-xl border-2 border-blue-200 p-8 text-center">
          <BookOpen className="h-16 w-16 text-blue-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Configurar Contabilidad</h2>
          <p className="text-gray-600 mb-6">
            Para comenzar a usar el módulo de contabilidad, primero debe cargar el plan de cuentas base.
          </p>
          <Link
            to="/contabilidad/config"
            className="inline-flex items-center gap-2 px-6 py-3 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50"
          >
            <Building2 className="h-5 w-5" />
            Configurar Plan de Cuentas
          </Link>
        </div>
      </div>
    );
  }

  const { periodo, asientos, pendientes, balances, anomalias, recientes } = data;

  return (
    <div className="space-y-6">
      {/* Period indicator */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">Período: {periodo?.nombre}</p>
        <button
          onClick={loadData}
          className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <RefreshCw className="h-4 w-4" />
          Actualizar
        </button>
      </div>

      {/* Anomalies Alert */}
      {anomalias?.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-yellow-800">Alertas Detectadas</h3>
              <ul className="mt-2 space-y-1">
                {anomalias.slice(0, 3).map((a, i) => (
                  <li key={i} className="text-sm text-yellow-700">
                    {a.mensaje}
                    {a.detalle && <span className="text-yellow-600"> - {a.detalle}</span>}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Balance Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-10 w-10 bg-blue-50 rounded-lg flex items-center justify-center">
              <TrendingUp className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Total Activos</p>
              <p className="text-xl font-bold text-gray-900">{formatCurrency(balances?.activo?.total)}</p>
            </div>
          </div>
          <p className="text-xs text-gray-500">
            {balances?.activo?.cuentas > 0
              ? `${balances?.activo?.cuentas} cuentas con saldo`
              : `${balances?.activo?.cuentasTotal || 0} cuentas configuradas`}
          </p>
        </div>

        <div className="bg-white rounded-xl border p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-10 w-10 bg-red-50 rounded-lg flex items-center justify-center">
              <Wallet className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Total Pasivos</p>
              <p className="text-xl font-bold text-gray-900">{formatCurrency(balances?.pasivo?.total)}</p>
            </div>
          </div>
          <p className="text-xs text-gray-500">
            {balances?.pasivo?.cuentas > 0
              ? `${balances?.pasivo?.cuentas} cuentas con saldo`
              : `${balances?.pasivo?.cuentasTotal || 0} cuentas configuradas`}
          </p>
        </div>

        <div className={`rounded-xl border p-5 ${
          balances?.cuadra ? 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-200' : 'bg-white'
        }`}>
          <div className="flex items-center gap-3 mb-3">
            <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
              balances?.cuadra ? 'bg-green-100' : 'bg-green-50'
            }`}>
              <PieChart className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Patrimonio + Utilidad</p>
              <p className="text-xl font-bold text-gray-900">
                {formatCurrency((balances?.patrimonio?.total || 0) + (balances?.utilidad || 0))}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1 text-xs">
            {balances?.cuadra ? (
              <>
                <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                <span className="text-green-700 font-medium">Balance cuadra correctamente</span>
              </>
            ) : (
              <>
                <AlertTriangle className="h-3.5 w-3.5 text-red-500" />
                <span className="text-red-600">Diferencia: {formatCurrency(Math.abs(balances?.diferencia || 0))}</span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Asientos Stats and Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Asientos Stats */}
        <div className="bg-white rounded-xl border p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Asientos del Período</h3>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <p className="text-2xl font-bold text-gray-900">{asientos?.total || 0}</p>
              <p className="text-xs text-gray-500">Total</p>
            </div>
            <div className="text-center p-3 bg-green-50 rounded-lg">
              <p className="text-2xl font-bold text-green-600">{asientos?.contabilizados || 0}</p>
              <p className="text-xs text-gray-500">Contabilizados</p>
            </div>
            <div className="text-center p-3 bg-yellow-50 rounded-lg">
              <p className="text-2xl font-bold text-yellow-600">{asientos?.borradores || 0}</p>
              <p className="text-xs text-gray-500">Borradores</p>
            </div>
          </div>

          <div className="mt-4 pt-4 border-t flex items-center justify-between text-sm">
            <span className="text-gray-500">
              {asientos?.generadosAi || 0} generados por AI
            </span>
            <Link
              to="/contabilidad/asientos"
              className="text-blue-600 hover:text-blue-700 flex items-center gap-1"
            >
              Ver todos <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>

        {/* Pending Documents & Borradores */}
        <div className="bg-white rounded-xl border p-6">
          <h3 className="font-semibold text-gray-900 mb-4">Pendientes de Contabilizar</h3>

          {(pendientes?.total > 0 || asientos?.borradores > 0) ? (
            <>
              <div className="space-y-3">
                {/* Borradores pendientes */}
                {asientos?.borradores > 0 && (
                  <Link
                    to="/contabilidad/asientos?estado=borrador"
                    className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg hover:bg-yellow-100 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <Clock className="h-5 w-5 text-yellow-600" />
                      <span className="text-sm font-medium">Asientos en Borrador</span>
                    </div>
                    <span className="text-lg font-bold text-yellow-600">
                      {asientos.borradores}
                    </span>
                  </Link>
                )}

                {/* Documentos sin asiento */}
                {(pendientes?.documentos?.length > 0) && (
                  <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <FileText className="h-5 w-5 text-blue-600" />
                      <span className="text-sm font-medium">Documentos de Venta</span>
                    </div>
                    <span className="text-lg font-bold text-blue-600">
                      {pendientes.documentos.length}
                    </span>
                  </div>
                )}

                {/* Compras sin asiento */}
                {(pendientes?.compras?.length > 0) && (
                  <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Calculator className="h-5 w-5 text-purple-600" />
                      <span className="text-sm font-medium">Facturas de Compra</span>
                    </div>
                    <span className="text-lg font-bold text-purple-600">
                      {pendientes.compras.length}
                    </span>
                  </div>
                )}
              </div>

              {pendientes?.total > 0 && (
                <div className="mt-4 pt-4 border-t">
                  <Link
                    to="/contabilidad/asientos?pendientes=true"
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50"
                  >
                    Generar Asientos Automáticos
                  </Link>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-6">
              <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-2" />
              <p className="text-gray-600">Todo al día</p>
              <p className="text-sm text-gray-500">No hay pendientes</p>
            </div>
          )}
        </div>
      </div>

      {/* Recent Asientos */}
      {recientes?.length > 0 && (
        <div className="bg-white rounded-xl border">
          <div className="p-4 border-b flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">Asientos Recientes</h3>
            <Link to="/contabilidad/asientos" className="text-sm text-blue-600 hover:text-blue-700">
              Ver todos
            </Link>
          </div>
          <div className="divide-y">
            {recientes.map((asiento) => (
              <Link
                key={asiento.id}
                to={`/contabilidad/asientos/${asiento.id}`}
                className="flex items-center justify-between p-4 hover:bg-gray-50"
              >
                <div className="flex items-center gap-3">
                  <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${
                    asiento.estado === 'contabilizado' ? 'bg-green-100' :
                    asiento.estado === 'anulado' ? 'bg-red-100' : 'bg-yellow-100'
                  }`}>
                    <BookOpen className={`h-4 w-4 ${
                      asiento.estado === 'contabilizado' ? 'text-green-600' :
                      asiento.estado === 'anulado' ? 'text-red-600' : 'text-yellow-600'
                    }`} />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      #{asiento.numero} - {asiento.concepto}
                    </p>
                    <p className="text-xs text-gray-500">
                      {new Date(asiento.fecha).toLocaleDateString('es-CR')} • {asiento.tipo}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium text-gray-900">
                    {formatCurrency(asiento.totalDebito)}
                  </p>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    asiento.estado === 'contabilizado' ? 'bg-green-100 text-green-700' :
                    asiento.estado === 'anulado' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'
                  }`}>
                    {asiento.estado}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Quick Links */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Link
          to="/contabilidad/cuentas"
          className="bg-white rounded-xl border p-4 hover:border-blue-300 hover:shadow-sm transition-all"
        >
          <BookOpen className="h-6 w-6 text-blue-600 mb-2" />
          <p className="font-medium text-gray-900">Plan de Cuentas</p>
          <p className="text-xs text-gray-500">Catálogo contable</p>
        </Link>

        <Link
          to="/contabilidad/balance"
          className="bg-white rounded-xl border p-4 hover:border-blue-300 hover:shadow-sm transition-all"
        >
          <PieChart className="h-6 w-6 text-green-600 mb-2" />
          <p className="font-medium text-gray-900">Balance General</p>
          <p className="text-xs text-gray-500">Posición financiera</p>
        </Link>

        <Link
          to="/contabilidad/resultados"
          className="bg-white rounded-xl border p-4 hover:border-blue-300 hover:shadow-sm transition-all"
        >
          <TrendingUp className="h-6 w-6 text-purple-600 mb-2" />
          <p className="font-medium text-gray-900">Estado de Resultados</p>
          <p className="text-xs text-gray-500">Pérdidas y ganancias</p>
        </Link>

        <Link
          to="/contabilidad/bancos"
          className="bg-white rounded-xl border p-4 hover:border-blue-300 hover:shadow-sm transition-all"
        >
          <Building2 className="h-6 w-6 text-indigo-600 mb-2" />
          <p className="font-medium text-gray-900">Bancos</p>
          <p className="text-xs text-gray-500">Conciliación bancaria</p>
        </Link>

        <Link
          to="/contabilidad/hacienda"
          className="bg-white rounded-xl border p-4 hover:border-blue-300 hover:shadow-sm transition-all"
        >
          <FileText className="h-6 w-6 text-red-600 mb-2" />
          <p className="font-medium text-gray-900">Reportes Hacienda</p>
          <p className="text-xs text-gray-500">IVA (D-150), retenciones (D-103), renta (D-101)</p>
        </Link>
      </div>
    </div>
  );
}
